// @flow
import React, { Fragment, useEffect, useRef } from 'react';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';

type Props = {
  color: Colors,
  hasFocus?: boolean,
  isRoot?: boolean,
  label: string,
  link?: {
    target?: string,
    trackingData?: ?TrackingData,
    url?: string,
  },
  nextPanel: string,
  onClick: (nextPanel: string) => void,
  setActiveItem: () => void,
  tabIndex?: number,
};

export const SideNavigationItem = (props: Props) => {
  const {
    color,
    hasFocus,
    isRoot,
    label,
    link = {},
    nextPanel,
    onClick,
    setActiveItem,
    tabIndex = 0,
  } = props;

  const buttonRef: { current: ?HTMLElement } = useRef(null);

  useEffect(
    () => {
      if (hasFocus && buttonRef && buttonRef.current) {
        buttonRef.current.focus();
      }
    },
    [hasFocus],
  );

  const handleClick = () => {
    onClick(nextPanel);
    setActiveItem();
  };

  const content = (
    <Fragment key={label}>
      <span
        className={classnames(`text-${color}`, 'font-bold font-sans text-md', {
          uppercase: isRoot,
        })}
      >
        {label}
      </span>
      <Icon color={COLORS.GRAY_DARKER} hasRtl name={ICONS.BOLDER_ARROW_FORTH} width="0.625rem" />
    </Fragment>
  );

  return link && link.url ? (
    <TrackClick
      {...link}
      as={DynamicLink}
      className="reset-anchor cursor-pointer flex items-center w-full h-full py-4 justify-between"
      data-testid="SideNavigationItem-subitem-link"
      onClick={handleClick}
      tabIndex={tabIndex}
    >
      {content}
    </TrackClick>
  ) : (
    <button
      className="reset-button w-full"
      data-testid="SideNavigationItem-subitem-button"
      onClick={handleClick}
      ref={buttonRef}
      tabIndex={tabIndex}
      type="button"
    >
      <div className="flex items-center w-full h-full py-4 justify-between">{content}</div>
    </button>
  );
};
